      import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import axios from 'axios';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Avatar, AvatarFallback } from './components/ui/avatar';
import { Separator } from './components/ui/separator';
import { Textarea } from './components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Label } from './components/ui/label';
// Removed shadcn Select imports (using native <select> to avoid portal issues)
import { toast, Toaster } from 'sonner';
import { Heart, MapPin, Home as HomeIcon, Phone, Mail, User, Plus, MessageCircle, Search, LogOut, LogIn, Map as MapIcon, Filter, SlidersHorizontal, X, Upload, Star, Settings, Edit, Trash2, Eye, Share2, ChevronLeft, ChevronRight } from 'lucide-react';
import Map from './components/Map';
import AccountForm from './components/AccountForm';
import PaymentPage from './payments/PaymentPage';
import PaymentButton from './payments/PaymentButton';
import { paymentService } from './payments/paymentService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

// Auth context
const AuthContext = React.createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      // Pas de toast de succès ici: pour éviter le montage d'un portail pendant la redirection
      return true;
    } catch (error) {
      toast.error('Erreur de connexion');
      return false;
    }
  };

  const register = async (name, email, password, phone) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { name, email, password, phone });
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      // Pas de toast de succès ici: pour éviter le montage d'un portail pendant la redirection
      return true;
    } catch (error) {
      toast.error('Erreur d\'inscription');
      return false;
    }
  };

  const logout = () => {
    try {
      // Clear auth synchronously
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      // Avoid intermediate React state changes that can conflict with open dialogs/portals
      // Do a full navigation reset which is the most robust against DOM placement errors
      window.location.replace('/');
    } catch (error) {
      console.error('Logout error:', error);
      window.location.reload();
    }
  };

  // Mettre à jour le profil utilisateur
  const updateProfile = async ({ name, phone, password }) => {
    try {
      const payload = {};
      if (name !== undefined) payload.name = name;
      if (phone !== undefined) payload.phone = phone;
      if (password) payload.password = password;
      const response = await axios.patch(`${API}/auth/me`, payload);
      setUser(response.data);
      toast.success('Profil mis à jour');
      return true;
    } catch (error) {
      toast.error('Échec de la mise à jour du profil');
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// AccountForm externalized to ./components/AccountForm.jsx

// Header Component
const Header = ({ searchQuery, setSearchQuery, onSearch, activeView, setActiveView }) => {
  const { user, logout } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    onSearch();
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
      <div className="container mx-auto px-4 py-2 lg:py-3">
        {/* Desktop Layout - Unchanged */}
        <div className="hidden lg:flex items-center justify-between gap-2 sm:gap-4 min-w-0">
          {/* Logo */}
          <div className="flex items-center space-x-2">
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-1.5 rounded-lg shadow-md">
              <HomeIcon className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
            </div>
            <h1 className="text-lg lg:text-xl font-bold text-orange-600">
              IMMO&CO
            </h1>
          </div>

          {/* Search bar - Desktop */}
          <div className="flex-1 min-w-0 max-w-2xl xl:max-w-3xl mx-2 sm:mx-4">
            <form onSubmit={handleSearchSubmit} className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full h-10 pl-10 pr-20 sm:pr-24 text-sm bg-white border border-gray-300 rounded-full focus:border-orange-500 focus:ring-0"
                />
                <Button 
                  type="submit"
                  size="sm"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-orange-500 hover:bg-orange-600 h-8 px-3 rounded-full text-xs font-medium"
                >
                  OK
                </Button>
              </div>
            </form>
          </div>

          {/* Desktop navigation */}
          <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveView('home')}
                className={`text-gray-600 hover:text-orange-500 p-2 ${activeView === 'home' ? 'text-orange-500' : ''}`}
              >
                <HomeIcon className="w-4 h-4" />
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveView('search')}
                className={`text-gray-600 hover:text-orange-500 p-2 ${activeView === 'search' ? 'text-orange-500' : ''}`}
              >
                <Search className="w-4 h-4" />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveView('map')}
                className={`text-gray-600 hover:text-orange-500 p-2 ${activeView === 'map' ? 'text-orange-500' : ''}`}
              >
                <MapIcon className="w-4 h-4" />
              </Button>

              {user && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setActiveView('favorites')}
                    className={`text-gray-600 hover:text-orange-500 p-2 ${activeView === 'favorites' ? 'text-orange-500' : ''}`}
                  >
                    <Star className="w-4 h-4" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setActiveView('profile')}
                    className={`text-gray-600 hover:text-orange-500 p-2 ${activeView === 'profile' ? 'text-orange-500' : ''}`}
                  >
                    <HomeIcon className="w-4 h-4" />
                  </Button>

                  {user?.is_admin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setActiveView('admin')}
                      className={`text-gray-600 hover:text-orange-500 p-2 ${activeView === 'admin' ? 'text-orange-500' : ''}`}
                      title="Administration"
                    >
                      <Settings className="w-4 h-4" />
                    </Button>
                  )}

                  <Button
                    onClick={() => {
                      if (user?.is_blocked) {
                        toast.error("Votre compte est bloqué par l'administrateur");
                        return;
                      }
                      if (paymentService.getCredits() <= 0) {
                        toast.error('Paiement requis : 10 000 FCFA par publication');
                        window.location.assign('/payment?purchase=credit');
                        return;
                      }
                      setPublishOpen(true);
                    }}
                    disabled={!!user?.is_blocked}
                    className="bg-blue-600 hover:bg-blue-700 h-8 px-3 rounded text-xs font-medium ml-2"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Publier
                  </Button>

                  <div className="flex items-center gap-1 ml-2 pl-2 border-l border-gray-300">
                    <Avatar className="w-6 h-6">
                      <AvatarFallback className="bg-blue-100 text-blue-600 text-xs">
                        {user.name[0].toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <Button
                      onClick={() => logout()}
                      variant="ghost"
                      size="sm"
                      className="text-gray-600 hover:text-red-600 p-1"
                    >
                      <LogOut className="w-3 h-3" />
                    </Button>
                  </div>
                </>
              )}

              {!user && (
                <>
                  <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="h-8 px-3 text-xs">
                        Connexion
                      </Button>
                    </DialogTrigger>
                    <LoginDialog onClose={() => setLoginOpen(false)} />
                  </Dialog>

                  <Dialog open={registerOpen} onOpenChange={setRegisterOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-8 px-3 text-xs">
                        S'inscrire
                      </Button>
                    </DialogTrigger>
                    <RegisterDialog onClose={() => setRegisterOpen(false)} />
                  </Dialog>
                </>
              )}
            </div>
          </div>

        {/* Mobile Layout - Reorganized */}
        <div className="lg:hidden">
          {/* Top row: Logo + Menu + Publish button */}
          <div className="flex items-center justify-between gap-2">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-1.5 rounded-lg shadow-md">
                <HomeIcon className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-lg font-bold text-orange-600">
                IMMO&CO
              </h1>
            </div>

            {/* Right side: Menu + Publish */}
            <div className="flex items-center gap-2">
              {user && (
                <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
                  <Button
                    onClick={() => {
                      if (user?.is_blocked) {
                        toast.error("Votre compte est bloqué par l'administrateur");
                        return;
                      }
                      if (paymentService.getCredits() <= 0) {
                        toast.error('Paiement requis : 10 000 FCFA par publication');
                        window.location.assign('/payment?purchase=credit');
                        return;
                      }
                      setPublishOpen(true);
                    }}
                    disabled={!!user?.is_blocked}
                    className="bg-blue-600 hover:bg-blue-700 h-9 w-9 p-0 rounded-full"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                  <PublishDialog onClose={() => setPublishOpen(false)} />
                </Dialog>
              )}

              <Button
                variant="ghost"
                size="sm"
                className="p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <SlidersHorizontal className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Search bar - Mobile (below logo/publish) */}
          <div className="mt-3">
            <form onSubmit={handleSearchSubmit} className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher une annonce..."
                  className="w-full h-12 pl-11 pr-24 text-base bg-white border border-gray-300 rounded-full focus:border-orange-500 focus:ring-0"
                />
                <Button 
                  type="submit"
                  size="sm"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-orange-500 hover:bg-orange-600 h-10 px-4 rounded-full text-sm font-medium"
                >
                  OK
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-col space-y-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setActiveView('home');
                  setMobileMenuOpen(false);
                }}
                className="justify-start text-gray-600 hover:text-orange-500"
              >
                <HomeIcon className="w-4 h-4 mr-2" />
                Accueil
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setActiveView('search');
                  setMobileMenuOpen(false);
                }}
                className="justify-start text-gray-600 hover:text-orange-500"
              >
                <Search className="w-4 h-4 mr-2" />
                Recherche
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setActiveView('map');
                  setMobileMenuOpen(false);
                }}
                className="justify-start text-gray-600 hover:text-orange-500"
              >
                <MapIcon className="w-4 h-4 mr-2" />
                Carte
              </Button>
              {user && (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setActiveView('favorites');
                      setMobileMenuOpen(false);
                    }}
                    className="justify-start text-gray-600 hover:text-orange-500"
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Favoris
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setActiveView('profile');
                      setMobileMenuOpen(false);
                    }}
                    className="justify-start text-gray-600 hover:text-orange-500"
                  >
                    <HomeIcon className="w-4 h-4 mr-2" />
                    Mes annonces
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setActiveView('account');
                      setMobileMenuOpen(false);
                    }}
                    className="justify-start text-gray-600 hover:text-orange-500"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Mon compte
                  </Button>

                  {user?.is_admin && (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setActiveView('admin');
                        setMobileMenuOpen(false);
                      }}
                      className="justify-start text-gray-600 hover:text-orange-500"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Administration
                    </Button>
                  )}

                  <Button
                    variant="ghost"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      logout();
                    }}
                    className="justify-start text-red-600 hover:text-red-700"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Déconnexion
                  </Button>
                </>
              )}

              {!user && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setLoginOpen(true);
                    }}
                    className="justify-start"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Connexion
                  </Button>

                  <Button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setRegisterOpen(true);
                    }}
                    className="justify-start bg-blue-600 hover:bg-blue-700"
                  >
                    <User className="w-4 h-4 mr-2" />
                    S'inscrire
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      className="relative bg-orange-600 text-white py-12 sm:py-16 lg:py-20"
      style={{
        backgroundImage: `linear-gradient(rgba(251, 146, 60, 0.8), rgba(251, 146, 60, 0.9)), url('https://images.unsplash.com/photo-1580587771525-78b9dba3b914')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold tracking-tight mb-4 sm:mb-6">
          Trouvez votre prochain bien
        </h1>
        <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl opacity-95 max-w-2xl md:max-w-3xl lg:max-w-4xl mx-auto leading-relaxed">
          Explorez les meilleures offres de locations et de ventes, comparez les annonces et contactez facilement les propriétaires.
        </p>
      </div>
    </section>
  );
};

// Filters Sidebar
const FiltersSidebar = ({ filters, setFilters, onApplyFilters, cities, neighborhoods }) => {
  const resetFilters = () => {
    setFilters({
      city: '',
      neighborhood: '',
      listing_type: '',
      price_min: '',
      price_max: '',
      surface_min: '',
      surface_max: '',
      rooms_min: '',
      rooms_max: ''
    });
  };

  return (
    <div className="w-full lg:w-80 bg-white border-r border-gray-200 p-4 sm:p-6 lg:h-screen lg:overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Filtres</h3>
        <Button variant="outline" size="sm" onClick={resetFilters}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-6">
        <div>
          <Label className="text-sm font-medium text-gray-700 mb-2 block">Type de transaction</Label>
          <div className="space-y-2">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input 
                type="radio" 
                name="listing_type" 
                value=""
                checked={filters.listing_type === ''}
                onChange={(e) => setFilters({...filters, listing_type: e.target.value})}
                className="text-orange-500 w-4 h-4"
              />
              <span className="text-sm">Tous les types</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input 
                type="radio" 
                name="listing_type" 
                value="sale"
                checked={filters.listing_type === 'sale'}
                onChange={(e) => setFilters({...filters, listing_type: e.target.value})}
                className="text-orange-500 w-4 h-4"
              />
              <span className="text-sm">Vente</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input 
                type="radio" 
                name="listing_type" 
                value="rent"
                checked={filters.listing_type === 'rent'}
                onChange={(e) => setFilters({...filters, listing_type: e.target.value})}
                className="text-orange-500 w-4 h-4"
              />
              <span className="text-sm">Location</span>
            </label>
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium text-gray-700 mb-2 block">Ville</Label>
          <select
            value={filters.city || 'all'}
            onChange={(e) => setFilters({ ...filters, city: e.target.value === 'all' ? '' : e.target.value })}
            className="w-full h-10 sm:h-11 border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
          >
            <option value="all">Toutes les villes</option>
            {cities.map((city, index) => (
              <option key={`city-${index}-${city}`} value={city}>{city}</option>
            ))}
          </select>
        </div>

        <div>
          <Label className="text-sm font-medium text-gray-700 mb-2 block">Quartier</Label>
          <select
            value={filters.neighborhood || 'all'}
            onChange={(e) => setFilters({ ...filters, neighborhood: e.target.value === 'all' ? '' : e.target.value })}
            className="w-full h-10 sm:h-11 border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
          >
            <option value="all">Tous les quartiers</option>
            {neighborhoods.map((neighborhood, index) => (
              <option key={`neighborhood-${index}-${neighborhood}`} value={neighborhood}>{neighborhood}</option>
            ))}
          </select>
        </div>

        <div>
          <Label className="text-sm font-medium text-gray-700 mb-2 block">Prix (XAF)</Label>
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <Input
              type="number"
              placeholder="Min"
              value={filters.price_min}
              onChange={(e) => setFilters({...filters, price_min: e.target.value})}
              className="h-10 sm:h-11 text-sm"
            />
            <Input
              type="number"
              placeholder="Max"
              value={filters.price_max}
              onChange={(e) => setFilters({...filters, price_max: e.target.value})}
              className="h-10 sm:h-11 text-sm"
            />
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium text-gray-700 mb-2 block">Surface (m²)</Label>
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <Input
              type="number"
              placeholder="Min"
              value={filters.surface_min}
              onChange={(e) => setFilters({...filters, surface_min: e.target.value})}
              className="h-10 sm:h-11 text-sm"
            />
            <Input
              type="number"
              placeholder="Max"
              value={filters.surface_max}
              onChange={(e) => setFilters({...filters, surface_max: e.target.value})}
              className="h-10 sm:h-11 text-sm"
            />
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium text-gray-700 mb-2 block">Nombre de pièces</Label>
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <Input
              type="number"
              placeholder="Min"
              value={filters.rooms_min}
              onChange={(e) => setFilters({...filters, rooms_min: e.target.value})}
              className="h-10 sm:h-11 text-sm"
            />
            <Input
              type="number"
              placeholder="Max"
              value={filters.rooms_max}
              onChange={(e) => setFilters({...filters, rooms_max: e.target.value})}
              className="h-10 sm:h-11 text-sm"
            />
          </div>
        </div>

        <Button 
          onClick={onApplyFilters} 
          className="w-full bg-orange-500 hover:bg-orange-600 h-10 sm:h-11 text-sm font-medium"
        >
          Appliquer les filtres
        </Button>
      </div>
    </div>
  );
};

// Login Dialog
const LoginDialog = ({ onClose }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const success = await login(email, password);
      if (success) {
        // Redirection immédiate pour éviter toute réconciliation React avec des portails encore montés
        window.location.replace('/');
        return;
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <DialogContent className="max-w-md w-full mx-4 sm:mx-auto">
      <DialogHeader>
        <DialogTitle className="text-lg sm:text-xl">Connexion</DialogTitle>
        <DialogDescription className="text-sm sm:text-base">
          Connectez-vous à votre compte
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        <div>
          <Label htmlFor="email" className="text-sm font-medium">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="h-11 sm:h-12 text-base mt-1"
          />
        </div>
        <div>
          <Label htmlFor="password" className="text-sm font-medium">Mot de passe</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="h-11 sm:h-12 text-base mt-1"
          />
        </div>
        <DialogFooter>
          <Button type="submit" disabled={loading} className="w-full h-11 sm:h-12 text-base font-medium">
            {loading ? 'Connexion...' : 'Se connecter'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
};

// Register Dialog
const RegisterDialog = ({ onClose }) => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const success = await register(name, email, password, phone);
      if (success) {
        // Redirection immédiate pour éviter toute réconciliation React avec des portails encore montés
        window.location.replace('/');
        return;
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <DialogContent className="max-w-md w-full mx-4 sm:mx-auto">
      <DialogHeader>
        <DialogTitle className="text-lg sm:text-xl">Inscription</DialogTitle>
        <DialogDescription className="text-sm sm:text-base">
          Créez votre compte
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        <div>
          <Label htmlFor="name" className="text-sm font-medium">Nom complet</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="h-11 sm:h-12 text-base mt-1"
          />
        </div>
        <div>
          <Label htmlFor="email" className="text-sm font-medium">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="h-11 sm:h-12 text-base mt-1"
          />
        </div>
        <div>
          <Label htmlFor="phone" className="text-sm font-medium">Téléphone</Label>
          <Input
            id="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="h-11 sm:h-12 text-base mt-1"
          />
        </div>
        <div>
          <Label htmlFor="password" className="text-sm font-medium">Mot de passe</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="h-11 sm:h-12 text-base mt-1"
          />
        </div>
        <DialogFooter>
          <Button type="submit" disabled={loading} className="w-full h-11 sm:h-12 text-base font-medium">
            {loading ? 'Inscription...' : 'S\'inscrire'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
};

// Publish Dialog with Image Upload
const PublishDialog = ({ onClose, editListing = null }) => {
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState(editListing?.title || '');
  const [description, setDescription] = useState(editListing?.description || '');
  const [listingType, setListingType] = useState(editListing?.listing_type || 'sale');
  const [price, setPrice] = useState(editListing?.price?.toString() || '');
  const [city, setCity] = useState(editListing?.city || '');
  const [neighborhood, setNeighborhood] = useState(editListing?.neighborhood || '');
  const [contactPhone, setContactPhone] = useState(editListing?.phone || editListing?.user?.phone || '');
  const [contactEmail, setContactEmail] = useState(editListing?.email || editListing?.user?.email || '');
  const [bathrooms, setBathrooms] = useState(editListing?.bathrooms?.toString() || '');
  const [kitchenEquipped, setKitchenEquipped] = useState(editListing?.kitchen_equipped || false);
  const [balcony, setBalcony] = useState(editListing?.balcony || false);
  const [floor, setFloor] = useState(editListing?.floor?.toString() || '');
  const [totalFloors, setTotalFloors] = useState(editListing?.total_floors?.toString() || '');
  const [yearBuilt, setYearBuilt] = useState(editListing?.year_built?.toString() || '');
  const [availability, setAvailability] = useState(editListing?.availability || 'immediate');
  const [heating, setHeating] = useState(editListing?.heating || '');
  const [ac, setAc] = useState(editListing?.ac || false);
  const [parking, setParking] = useState(editListing?.parking || false);
  const [garage, setGarage] = useState(editListing?.garage || false);
  const [amenities, setAmenities] = useState(editListing?.amenities || []);
  const [address, setAddress] = useState(editListing?.address || '');
  const [surface, setSurface] = useState(editListing?.surface?.toString() || '');
  const [rooms, setRooms] = useState(editListing?.rooms?.toString() || '');
  const [placedMarker, setPlacedMarker] = useState(
    editListing?.lat && editListing?.lon ? { lat: editListing.lat, lng: editListing.lon } : null
  );
  const [isPlacingMarker, setIsPlacingMarker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + selectedFiles.length > 10) {
      toast.error('Maximum 10 fichiers autorisés');
      return;
    }
    setSelectedFiles([...selectedFiles, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const handleMapClick = async (latlng) => {
    setPlacedMarker(latlng);
    setIsPlacingMarker(false);
    
    try {
      const response = await axios.get(`${API}/reverse-geocode?lat=${latlng.lat}&lon=${latlng.lng}`);
      const data = response.data;
      setCity(data.city || '');
      setNeighborhood(data.neighborhood || '');
      setAddress(data.address || '');
      toast.success('Localisation définie !');
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      toast.error('Impossible de récupérer les informations de localisation');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!editListing && paymentService.getCredits() <= 0) {
      toast.error('Paiement requis : 10 000 FCFA par publication');
      setLoading(false);
      window.location.assign('/payment?purchase=credit');
      return;
    }

    // Validation des champs obligatoires
    if (!contactPhone.trim()) {
      toast.error('Le numéro de téléphone est obligatoire');
      setLoading(false);
      return;
    }
    if (!contactEmail.trim()) {
      toast.error('L\'email est obligatoire');
      setLoading(false);
      return;
    }

    try {
      const listingData = {
        title,
        description,
        listing_type: listingType,
        price: parseFloat(price),
        city,
        neighborhood,
        address,
        lat: placedMarker?.lat || null,
        lon: placedMarker?.lng || null,
        surface: surface ? parseInt(surface) : null,
        rooms: rooms ? parseInt(rooms) : null,
        bathrooms: bathrooms ? parseInt(bathrooms) : null,
        kitchen_equipped: kitchenEquipped,
        balcony: balcony,
        floor: floor ? parseInt(floor) : null,
        total_floors: totalFloors ? parseInt(totalFloors) : null,
        year_built: yearBuilt ? parseInt(yearBuilt) : null,
        availability: availability,
        heating: heating || null,
        ac: ac,
        parking: parking,
        garage: garage,
        amenities: amenities,
        phone: contactPhone,
        email: contactEmail
      };

      let listingResponse;
      if (editListing) {
        listingResponse = await axios.put(`${API}/listings/${editListing.id}`, listingData);
      } else {
        listingResponse = await axios.post(`${API}/listings`, listingData);
      }

      // Upload files if any
      if (selectedFiles.length > 0) {
        const formData = new FormData();
        selectedFiles.forEach(file => {
          formData.append('files', file);
        });

        await axios.post(`${API}/listings/${listingResponse.data.id}/images`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      if (!editListing) {
        paymentService.consumeCredit();
      }

      // Redirection immédiate pour éviter toute réconciliation React/portails pendant la fermeture du dialog
      window.location.replace('/');
      return;
    } catch (error) {
      toast.error('Erreur lors de la publication');
    }

    setLoading(false);
  };

  return (
    <DialogContent className="max-w-4xl w-full mx-4 sm:mx-auto max-h-[90vh] sm:max-h-[85vh] overflow-y-auto">
      <DialogHeader className="pb-4 sm:pb-6">
        <DialogTitle className="text-lg sm:text-xl lg:text-2xl">{editListing ? 'Modifier l\'annonce' : 'Déposer une annonce'}</DialogTitle>
        <DialogDescription className="text-sm sm:text-base">
          {step === 1 ? 'Remplissez les informations de votre bien' : 'Localisez votre bien sur la carte'}
        </DialogDescription>
      </DialogHeader>

      {step === 1 ? (
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div className="lg:col-span-2">
              <Label htmlFor="title" className="text-sm font-medium">Titre de l'annonce *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex: Belle villa avec piscine à Libreville"
                required
                className="h-11 sm:h-12 text-base mt-1"
              />
            </div>

            <div className="lg:col-span-2">
              <Label htmlFor="description" className="text-sm font-medium">Description *</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Décrivez votre bien en détail..."
                rows={4}
                required
                className="text-base mt-1 resize-y min-h-[100px]"
              />
            </div>

            {/* Coordonnées de contact (optionnel) */}
            <div>
              <Label htmlFor="contactPhone" className="text-sm font-medium">Téléphone *</Label>
              <Input
                id="contactPhone"
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="Ex: +241 XX XX XX XX"
                required
                className="h-11 sm:h-12 text-base mt-1"
              />
            </div>
            <div>
              <Label htmlFor="contactEmail" className="text-sm font-medium">Email *</Label>
              <Input
                id="contactEmail"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="Ex: contact@email.com"
                required
                className="h-11 sm:h-12 text-base mt-1"
              />
            </div>

            {/* Caractéristiques avancées */}
            <div>
              <Label htmlFor="bathrooms" className="text-sm font-medium">Salles de bain</Label>
              <Input
                id="bathrooms"
                type="number"
                value={bathrooms}
                onChange={(e) => setBathrooms(e.target.value)}
                placeholder="Nombre de salles de bain"
                className="h-11 sm:h-12 text-base mt-1"
              />
            </div>
            <div>
              <Label htmlFor="floor" className="text-sm font-medium">Étage</Label>
              <Input
                id="floor"
                type="number"
                value={floor}
                onChange={(e) => setFloor(e.target.value)}
                placeholder="Étage du bien"
                className="h-11 sm:h-12 text-base mt-1"
              />
            </div>
            <div>
              <Label htmlFor="totalFloors" className="text-sm font-medium">Nombre d'étages total</Label>
              <Input
                id="totalFloors"
                type="number"
                value={totalFloors}
                onChange={(e) => setTotalFloors(e.target.value)}
                placeholder="Nombre total d'étages"
                className="h-11 sm:h-12 text-base mt-1"
              />
            </div>
            <div>
              <Label htmlFor="yearBuilt" className="text-sm font-medium">Année de construction</Label>
              <Input
                id="yearBuilt"
                type="number"
                value={yearBuilt}
                onChange={(e) => setYearBuilt(e.target.value)}
                placeholder="Ex: 2020"
                className="h-11 sm:h-12 text-base mt-1"
              />
            </div>

            {/* Options booléennes */}
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  id="kitchenEquipped"
                  checked={kitchenEquipped}
                  onChange={(e) => setKitchenEquipped(e.target.checked)}
                  className="rounded w-4 h-4 sm:w-5 sm:h-5"
                />
                <Label htmlFor="kitchenEquipped" className="text-sm cursor-pointer">Cuisine équipée</Label>
              </div>
              <div className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  id="balcony"
                  checked={balcony}
                  onChange={(e) => setBalcony(e.target.checked)}
                  className="rounded w-4 h-4 sm:w-5 sm:h-5"
                />
                <Label htmlFor="balcony" className="text-sm cursor-pointer">Balcon/Terrasse</Label>
              </div>
              <div className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  id="ac"
                  checked={ac}
                  onChange={(e) => setAc(e.target.checked)}
                  className="rounded w-4 h-4 sm:w-5 sm:h-5"
                />
                <Label htmlFor="ac" className="text-sm cursor-pointer">Climatisation</Label>
              </div>
              <div className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  id="parking"
                  checked={parking}
                  onChange={(e) => setParking(e.target.checked)}
                  className="rounded w-4 h-4 sm:w-5 sm:h-5"
                />
                <Label htmlFor="parking" className="text-sm cursor-pointer">Parking</Label>
              </div>
              <div className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  id="garage"
                  checked={garage}
                  onChange={(e) => setGarage(e.target.checked)}
                  className="rounded w-4 h-4 sm:w-5 sm:h-5"
                />
                <Label htmlFor="garage" className="text-sm cursor-pointer">Garage</Label>
              </div>
            </div>

            {/* Disponibilité et chauffage */}
            <div>
              <Label htmlFor="availability" className="text-sm font-medium">Disponibilité</Label>
              <select
                id="availability"
                value={availability}
                onChange={(e) => setAvailability(e.target.value)}
                className="w-full h-11 sm:h-12 border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 text-base mt-1"
              >
                <option value="immediate">Immédiate</option>
                <option value="1month">Dans 1 mois</option>
                <option value="3months">Dans 3 mois</option>
                <option value="6months">Dans 6 mois</option>
                <option value="custom">À convenir</option>
              </select>
            </div>
            <div>
              <Label htmlFor="heating" className="text-sm font-medium">Chauffage</Label>
              <select
                id="heating"
                value={heating}
                onChange={(e) => setHeating(e.target.value)}
                className="w-full h-11 sm:h-12 border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 text-base mt-1"
              >
                <option value="">Sélectionner</option>
                <option value="none">Aucun</option>
                <option value="electric">Électrique</option>
                <option value="gas">Gaz</option>
                <option value="solar">Solaire</option>
                <option value="other">Autre</option>
              </select>
            </div>

            <div>
              <Label htmlFor="type" className="text-sm font-medium">Type d'annonce *</Label>
              <select
                id="type"
                value={listingType}
                onChange={(e) => setListingType(e.target.value)}
                className="w-full h-11 sm:h-12 border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 text-base mt-1"
                required
              >
                <option value="sale">Vente</option>
                <option value="rent">Location</option>
              </select>
            </div>

            <div>
              <Label htmlFor="price" className="text-sm font-medium">Prix (XAF) *</Label>
              <Input
                id="price"
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="Ex: 150000"
                required
                className="h-11 sm:h-12 text-base mt-1"
              />
            </div>

            <div>
              <Label htmlFor="city">Ville *</Label>
              <Input
                id="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Ex: Libreville"
                required
              />
            </div>

            <div>
              <Label htmlFor="neighborhood">Quartier</Label>
              <Input
                id="neighborhood"
                value={neighborhood}
                onChange={(e) => setNeighborhood(e.target.value)}
                placeholder="Ex: Batterie IV"
              />
            </div>

            <div>
              <Label htmlFor="surface">Surface (m²)</Label>
              <Input
                id="surface"
                type="number"
                value={surface}
                onChange={(e) => setSurface(e.target.value)}
                placeholder="Ex: 150"
              />
            </div>

            <div>
              <Label htmlFor="rooms">Nombre de pièces</Label>
              <Input
                id="rooms"
                type="number"
                value={rooms}
                onChange={(e) => setRooms(e.target.value)}
                placeholder="Ex: 4"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="address">Adresse complète</Label>
              <Input
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Adresse détaillée"
              />
            </div>

            <div className="md:col-span-2">
              <Label>Images et vidéos (max 10 fichiers)</Label>
              <div className="mt-2">
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                />
                {selectedFiles.length > 0 && (
                  <div className="mt-2 grid grid-cols-4 gap-2">
                    {selectedFiles.map((file, index) => (
                      <div key={`${file?.name || 'file'}-${index}`} className="relative">
                        <div className="bg-gray-100 p-2 rounded text-xs truncate">
                          {file.name}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-between pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep(2)}
              className="flex items-center"
            >
              <MapIcon className="w-4 h-4 mr-2" />
              Localiser sur la carte
            </Button>
            
            <div className="space-x-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Annuler
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Publication...' : (editListing ? 'Modifier' : 'Publier l\'annonce')}
              </Button>
            </div>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <div className="h-96">
            <Map
              onMapClick={handleMapClick}
              isPlacingMarker={isPlacingMarker}
              placedMarker={placedMarker}
              height="100%"
            />
          </div>
          
          <div className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep(1)}
            >
              Retour au formulaire
            </Button>
            
            <div className="space-x-2">
              <Button
                type="button"
                onClick={() => setIsPlacingMarker(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <MapIcon className="w-4 h-4 mr-2" />
                Placer un marqueur
              </Button>
              
              {placedMarker && (
                <Button
                  type="button"
                  onClick={() => setStep(1)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Confirmer la localisation
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </DialogContent>
  );
};

// Listing Card
const ListingCard = ({ listing, onMarkerClick, onView, showActions = false, onEdit, onDelete, commentsOpen = undefined, onToggleComments = undefined }) => {
  const { user } = useAuth();
  const [liked, setLiked] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [likesCount, setLikesCount] = useState(listing.likes_count || 0);
  const [comments, setComments] = useState([]);
  // If parent controls comment visibility, use that. Otherwise keep local state.
  const [internalShowComments, setInternalShowComments] = useState(false);
  const showComments = (commentsOpen !== undefined) ? commentsOpen : internalShowComments;
  const [newComment, setNewComment] = useState('');
  const [replyToId, setReplyToId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [showComposer, setShowComposer] = useState(false);

  useEffect(() => {
    if (user) {
      checkIfLiked();
      checkIfFavorite();
    }
  }, [listing.id, user]);

  const checkIfLiked = async () => {
    try {
      const response = await axios.get(`${API}/listings/${listing.id}/liked`);
      setLiked(response.data.liked);
    } catch (error) {
      console.error('Error checking like status:', error);
    }
  };

  const checkIfFavorite = async () => {
    try {
      const response = await axios.get(`${API}/favorites/${listing.id}/check`);
      setIsFavorite(response.data.is_favorite);
    } catch (error) {
      console.error('Error checking favorite status:', error);
    }
  };

  const toggleLike = async () => {
    if (!user) {
      toast.error('Connectez-vous pour liker');
      return;
    }
    if (user.is_blocked) {
      toast.error("Votre compte est bloqué par l'administrateur");
      return;
    }

    try {
      const response = await axios.post(`${API}/listings/${listing.id}/like`);
      setLiked(response.data.liked);
      setLikesCount(response.data.likes_count);
    } catch (error) {
      toast.error('Erreur lors du like');
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      toast.error('Connectez-vous pour ajouter aux favoris');
      return;
    }
    if (user.is_blocked) {
      toast.error("Votre compte est bloqué par l'administrateur");
      return;
    }

    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${listing.id}`);
        setIsFavorite(false);
        toast.success('Retiré des favoris');
      } else {
        await axios.post(`${API}/favorites/${listing.id}`);
        setIsFavorite(true);
        toast.success('Ajouté aux favoris');
      }
    } catch (error) {
      toast.error('Erreur lors de la gestion des favoris');
    }
  };

  const loadComments = async () => {
    try {
      const response = await axios.get(`${API}/listings/${listing.id}/comments`);
      setComments(response.data);
      if (onToggleComments) {
        onToggleComments(true);
      } else {
        setInternalShowComments(true);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Connectez-vous pour commenter');
      return;
    }
    if (user.is_blocked) {
      toast.error("Votre compte est bloqué par l'administrateur");
      return;
    }

    try {
      const response = await axios.post(`${API}/listings/${listing.id}/comments`, {
        text: newComment
      });
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success('Commentaire ajouté');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du commentaire');
    }
  };

  const addReply = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Connectez-vous pour répondre');
      return;
    }
    if (user.is_blocked) {
      toast.error("Votre compte est bloqué par l'administrateur");
      return;
    }
    if (!replyToId || !replyText.trim()) return;
    try {
      const response = await axios.post(`${API}/listings/${listing.id}/comments`, {
        text: replyText,
        parent_id: replyToId,
      });
      setComments([response.data, ...comments]);
      setReplyText('');
      setReplyToId(null);
      toast.success('Réponse envoyée');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de la réponse');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR').format(price) + ' XAF';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getImageUrl = (imageUrlOrFilename) => {
    // Si c'est déjà une URL complète (Cloudinary ou autre), la retourner directement
    if (imageUrlOrFilename && (imageUrlOrFilename.startsWith('http') || imageUrlOrFilename.startsWith('https'))) {
      return imageUrlOrFilename;
    }
    // Sinon, c'est un ancien fichier local, construire l'URL locale
    return `${BACKEND_URL}/uploads/${imageUrlOrFilename}`;
  };

  return (
    <Card 
      className={`overflow-hidden group relative ${onView ? 'cursor-pointer' : ''}`}
      onClick={() => onView && onView(listing)}
      role={onView ? 'button' : undefined}
      tabIndex={onView ? 0 : -1}
      onKeyDown={(e) => {
        if (!onView) return;
        // Ignore keyboard activation when focus is inside inputs or interactive fields
        const target = e.target;
        if (target && (target.closest('input, textarea, select, button, [contenteditable="true"]'))) {
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          onView(listing);
        }
      }}
    >
      {/* Image */}
      <div className="aspect-[4/3] bg-gradient-to-br from-orange-100 to-yellow-100 flex items-center justify-center relative">
        {listing.images && listing.images.length > 0 ? (
          <img 
            src={getImageUrl(listing.images[0])} 
            alt={listing.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <HomeIcon className="w-12 h-12 sm:w-16 sm:h-16 text-orange-400" />
        )}
        
        {listing.is_featured && (
          <div className="absolute top-2 left-2">
            <Badge className="bg-yellow-500 text-white flex items-center gap-1 text-xs">
              <Star className="w-3 h-3" />
              <span className="hidden sm:inline">Mis en avant</span>
              <span className="sm:hidden">En avant</span>
            </Badge>
          </div>
        )}

        <div className="absolute top-2 right-2 flex flex-col gap-1 sm:flex-row sm:gap-1">
          <Badge 
            variant={listing.listing_type === 'sale' ? 'default' : 'secondary'}
            className={`${listing.listing_type === 'sale' ? 'bg-blue-600' : 'bg-green-600'} text-xs`}
          >
            {listing.listing_type === 'sale' ? 'Vente' : 'Location'}
          </Badge>
          
          {user && (
            <Button
              size="sm"
              variant="secondary" 
              disabled={!!user?.is_blocked}
              className="h-7 w-7 sm:h-6 sm:w-6 p-0 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={(e) => { e.stopPropagation(); toggleFavorite(); }}
            >
              <Star className={`w-3 h-3 sm:w-4 sm:h-4 ${isFavorite ? 'fill-current text-yellow-500' : ''}`} />
            </Button>
          )}
        </div>

        {listing.lat && listing.lon && (
          <Button
            size="sm"
            variant="secondary" 
            className="absolute bottom-2 right-2 h-7 sm:h-8 px-2 text-xs"
            onClick={(e) => { e.stopPropagation(); onMarkerClick && onMarkerClick(listing); }}
          >
            <MapIcon className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
            <span className="hidden sm:inline">Carte</span>
          </Button>
        )}

        {listing.images && listing.images.length > 1 && (
          <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
            +{listing.images.length - 1} photos
          </div>
        )}
      </div>

      <CardContent className="p-3 sm:p-4">
        <div className="space-y-2 sm:space-y-3">
          <div>
            <h3 className="font-semibold text-base sm:text-lg line-clamp-2 text-gray-900">
              {listing.title}
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mt-1">
              {listing.description}
            </p>
          </div>

          <div className="text-xl sm:text-2xl font-bold text-orange-600">
            {formatPrice(listing.price)}
          </div>

          <div className="flex items-center text-xs sm:text-sm text-gray-600">
            <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-orange-500" />
            <span className="font-medium truncate">
              {listing.city}{listing.neighborhood && `, ${listing.neighborhood}`}
            </span>
          </div>

          {/* Informations de contact */}
          {(listing.phone || listing.email) && (
            <div className="bg-gray-50 rounded-lg p-2 sm:p-3 space-y-2">
              <div className="text-xs font-medium text-gray-700 mb-1 sm:mb-2">Contact</div>
              <div className="flex flex-wrap gap-1 sm:gap-2">
                {listing.phone && (
                  <a 
                    href={`tel:${listing.phone}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full hover:bg-green-200 transition-colors"
                  >
                    <Phone className="w-3 h-3" />
                    <span className="hidden sm:inline">{listing.phone}</span>
                    <span className="sm:hidden">Appeler</span>
                  </a>
                )}
                {listing.email && (
                  <a 
                    href={`mailto:${listing.email}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full hover:bg-blue-200 transition-colors"
                  >
                    <Mail className="w-3 h-3" />
                    <span className="hidden sm:inline">{listing.email}</span>
                    <span className="sm:hidden">Email</span>
                  </a>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm text-gray-500 pt-2 border-t border-gray-100 gap-2 sm:gap-0">
            <div className="flex space-x-3 sm:space-x-4">
              {listing.surface && (
                <span className="flex items-center">
                  {listing.surface} m²
                </span>
              )}
              {listing.rooms && (
                <span className="flex items-center">
                  {listing.rooms} pièces
                </span>
              )}
            </div>
            <span>{formatDate(listing.created_at)}</span>
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); toggleLike(); }}
                disabled={!!user?.is_blocked}
                className={`flex items-center space-x-1 h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm ${
                  liked ? 'text-red-500 hover:text-red-600' : 'text-gray-500 hover:text-red-500'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <Heart className={`w-3 h-3 sm:w-4 sm:h-4 ${liked ? 'fill-current' : ''}`} />
                <span className="text-xs sm:text-sm">{likesCount}</span>
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  if (showComments) {
                    if (onToggleComments) {
                      onToggleComments(false);
                    } else {
                      setInternalShowComments(false);
                    }
                    setShowComposer(false);
                  } else {
                    if (onToggleComments) {
                      onToggleComments(true);
                    } else {
                      setInternalShowComments(true);
                    }
                  }
                }}
                className={`flex items-center space-x-1 h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm ${
                  showComments ? 'text-blue-600' : 'text-gray-500'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <MessageCircle className="w-4 h-4" />
                <span>{listing.comments_count || 0}</span>
              </Button>
            </div>

            {showActions && (
              <div className="flex items-center space-x-1 sm:space-x-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => onEdit(listing)}
                  className="h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm"
                >
                  <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Modifier</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => onDelete(listing.id)}
                  className="h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm text-red-600 hover:text-red-700 border-red-300"
                >
                  <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Supprimer</span>
                </Button>
              </div>
            )}

            <div className="flex items-center text-sm text-gray-600">
              <User className="w-4 h-4 mr-1" />
              <span className="font-medium">{listing.owner_name}</span>
            </div>
          </div>

          {showComments && (
            <div className="space-y-3 pt-3 border-t border-gray-100 bg-gray-50 rounded-md p-3">
              {user && (
                !showComposer ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setShowComposer(true); }}
                    onKeyDown={(e) => e.stopPropagation()}
                    disabled={!!user?.is_blocked}
                    className="text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm"
                  >
                    Commenter
                  </Button>
                ) : (
                  <form onSubmit={addComment} className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
                    <Input
                      placeholder="Ajouter un commentaire..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      disabled={!!user?.is_blocked}
                      className="flex-1 h-8 sm:h-9 text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <Button 
                      type="submit" 
                      size="sm" 
                      disabled={!newComment.trim() || !!user?.is_blocked}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Envoyer
                    </Button>
                    <Button 
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); setShowComposer(false); setNewComment(''); }}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="text-gray-500 h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm"
                    >
                      Annuler
                    </Button>
                  </form>
                )
              )}

              <div className="space-y-2 max-h-48 overflow-y-auto">
                {comments.filter(c => !c.parent_id).map((comment) => (
                  <div key={comment.id} className="text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{comment.author_name}</span>
                        <span className="text-gray-400 text-xs">
                          {formatDate(comment.created_at)}
                        </span>
                      </div>
                      {user?.is_admin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await axios.delete(`${API}/admin/comments/${comment.id}`);
                              setComments((prev) => prev.filter(c => c.id !== comment.id && c.parent_id !== comment.id));
                              toast.success('Commentaire supprimé');
                            } catch (err) {
                              toast.error('Erreur suppression commentaire');
                            }
                          }}
                          className="text-xs text-red-500 hover:text-red-600"
                        >
                          Supprimer
                        </Button>
                      )}
                    </div>
                    <p className="text-gray-700">{comment.text}</p>
                    <div className="mt-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setReplyToId(comment.id); }}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >
                        Répondre
                      </Button>
                      {replyToId === comment.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setReplyToId(null); setReplyText(''); }}
                          className="text-xs text-gray-500 hover:text-gray-700 ml-2"
                        >
                          Annuler
                        </Button>
                      )}
                    </div>
                    
                    {/* Réponses */}
                    {comments.filter(c => c.parent_id === comment.id).map((reply) => (
                      <div key={reply.id} className="ml-4 mt-2 pl-3 border-l-2 border-gray-200">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-medium text-xs">{reply.author_name}</span>
                          <span className="text-gray-400 text-xs">
                            {formatDate(reply.created_at)}
                          </span>
                          {user?.is_admin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await axios.delete(`${API}/admin/comments/${reply.id}`);
                                  setComments((prev) => prev.filter(c => c.id !== reply.id));
                                  toast.success('Réponse supprimée');
                                } catch (err) {
                                  toast.error('Erreur suppression réponse');
                                }
                              }}
                              className="text-[10px] text-red-500 hover:text-red-600"
                            >
                              Supprimer
                            </Button>
                          )}
                        </div>
                        <p className="text-gray-600 text-xs">{reply.text}</p>
                      </div>
                    ))}
                    
                    {/* Formulaire de réponse */}
                    {replyToId === comment.id && (
                      <div className="ml-4 mt-2" onClick={(e) => e.stopPropagation()}>
                        <form onSubmit={addReply} className="flex space-x-2">
                          <Input
                            placeholder="Votre réponse..."
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            disabled={!!user?.is_blocked}
                            className="flex-1 text-sm disabled:opacity-50"
                          />
                          <Button 
                            type="submit" 
                            size="sm" 
                            disabled={!replyText.trim() || !!user?.is_blocked}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            Répondre
                          </Button>
                        </form>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Admin Panel Component
const AdminPanel = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [adminListings, setAdminListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [previewListing, setPreviewListing] = useState(null);

  useEffect(() => {
    if (user?.is_admin) {
      fetchAdminData();
    }
  }, [user]);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      const [usersRes, listingsRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/listings`)
      ]);
      setUsers(usersRes.data);
      setAdminListings(listingsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données admin');
    } finally {
      setLoading(false);
    }
  };

  const toggleAdminStatus = async (userId) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/admin`);
      toast.success('Statut admin mis à jour');
      fetchAdminData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteAdminListing = async (listingId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette annonce ?')) return;
    
    try {
      await axios.delete(`${API}/admin/listings/${listingId}`);
      toast.success('Annonce supprimée');
      fetchAdminData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const togglePublish = async (listing) => {
    try {
      const next = !listing.is_published;
      await axios.patch(`${API}/admin/listings/${listing.id}/publish`, null, { params: { publish: next } });
      toast.success(next ? 'Annonce publiée' : 'Annonce masquée');
      fetchAdminData();
    } catch (e) {
      toast.error('Erreur de publication');
    }
  };

  const toggleFeature = async (listing) => {
    try {
      const next = !listing.is_featured;
      await axios.patch(`${API}/admin/listings/${listing.id}/feature`, null, { params: { featured: next } });
      toast.success(next ? 'Mise en avant activée' : 'Mise en avant retirée');
      fetchAdminData();
    } catch (e) {
      toast.error('Erreur mise en avant');
    }
  };

  const updateCategory = async (listing, category) => {
    try {
      await axios.patch(`${API}/admin/listings/${listing.id}/category`, null, { params: { category } });
      toast.success('Catégorie mise à jour');
      fetchAdminData();
    } catch (e) {
      toast.error('Erreur catégorie');
    }
  };

  const toggleBlockUser = async (userId) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/block`);
      // Optimistic UI update
      setUsers((prev) => prev.map(u => u.id === userId ? { ...u, is_blocked: !u.is_blocked } : u));
      toast.success('Statut de blocage mis à jour');
    } catch (e) {
      toast.error('Erreur blocage utilisateur');
    }
  };

  

  if (!user?.is_admin) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8">
          <div className="text-red-600 mb-4">
            <Settings className="w-16 h-16 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-red-800 mb-2">Accès refusé</h2>
          <p className="text-red-600">Vous n'avez pas les permissions d'administrateur.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p>Chargement des données d'administration...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Administration</h1>
        <p className="text-gray-600">Gestion des utilisateurs et des annonces</p>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Utilisateurs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{users.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Administrateurs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{users.filter(u => u.is_admin).length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Annonces</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{adminListings.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Annonces Publiées</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{adminListings.filter(l => l.is_published).length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Onglets */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('users')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'users'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Utilisateurs ({users.length})
            </button>
            <button
              onClick={() => setActiveTab('listings')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'listings'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Annonces ({adminListings.length})
            </button>
          </nav>
        </div>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'users' && (
        <Card>
          <CardHeader>
            <CardTitle>Gestion des utilisateurs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Nom</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Email</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Téléphone</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Statut</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">{u.name}</td>
                      <td className="py-3 px-4">{u.email}</td>
                      <td className="py-3 px-4">{u.phone || '-'}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-8 h-8">
                            <AvatarFallback>{u.name[0]?.toUpperCase() || '?'}</AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              <span>{u.name}</span>
                              {u.is_admin && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Admin</span>
                              )}
                              {u.is_blocked && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700">Bloqué</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 space-x-2">
                        <Button size="sm" variant="outline" onClick={() => toggleAdminStatus(u.id)} className="text-xs">
                          {u.is_admin ? 'Retirer admin' : 'Promouvoir admin'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => toggleBlockUser(u.id)} className="text-xs">
                          {u.is_blocked ? 'Débloquer' : 'Bloquer'}
                        </Button>
                        
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'listings' && (
        <Card>
          <CardHeader>
            <CardTitle>Gestion des annonces</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {adminListings.map((listing) => (
                <Card key={listing.id} className="relative cursor-pointer" onClick={() => setPreviewListing(listing)}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg truncate">{listing.title}</CardTitle>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteAdminListing(listing.id)}
                        className="ml-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <p><strong>Propriétaire:</strong> {listing.owner_name}</p>
                      <p><strong>Email:</strong> {listing.owner_email}</p>
                      <p><strong>Prix:</strong> {new Intl.NumberFormat('fr-FR').format(listing.price)} XAF</p>
                      <p><strong>Type:</strong> {listing.listing_type === 'sale' ? 'Vente' : 'Location'}</p>
                      <p><strong>Ville:</strong> {listing.city}</p>
                      <div className="flex justify-between items-center pt-2">
                        <Badge variant={listing.is_published ? 'default' : 'secondary'}>
                          {listing.is_published ? 'Publié' : 'Brouillon'}
                        </Badge>
                        <div className="flex space-x-2 text-xs text-gray-500">
                          <span>❤️ {listing.likes_count}</span>
                          <span>💬 {listing.comments_count}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 pt-3">
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); togglePublish(listing); }}>
                          {listing.is_published ? 'Masquer' : 'Publier'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); toggleFeature(listing); }}>
                          {listing.is_featured ? 'Retirer mise en avant' : 'Mettre en avant'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview listing dialog */}
      <Dialog open={!!previewListing} onOpenChange={(open) => !open && setPreviewListing(null)}>
        {previewListing && (
          <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto p-0">
            <DialogHeader>
              <DialogTitle>{previewListing.title}</DialogTitle>
              <DialogDescription>{previewListing.city} • {previewListing.listing_type === 'sale' ? 'Vente' : 'Location'}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 p-4">
              {previewListing.images && previewListing.images.length > 0 && (
                <div className="w-full aspect-[4/2] bg-gray-100 rounded-lg overflow-hidden">
                  <img src={getImageUrl(previewListing.images[0])} alt={previewListing.title} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-3">
                  <p className="text-gray-700 leading-relaxed">{previewListing.description}</p>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Ville</span><span className="font-medium">{previewListing.city}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Type</span><span className="font-medium">{previewListing.listing_type === 'sale' ? 'Vente' : 'Location'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Prix</span><span className="font-semibold">{new Intl.NumberFormat('fr-FR').format(previewListing.price)} XAF</span></div>
                  {previewListing.category && (
                    <div className="flex justify-between"><span className="text-gray-500">Catégorie</span><span className="font-medium">{previewListing.category}</span></div>
                  )}
                  <div className="flex items-center gap-2 pt-2">
                    <Badge variant={previewListing.is_published ? 'default' : 'secondary'}>
                      {previewListing.is_published ? 'Publié' : 'Brouillon'}
                    </Badge>
                    {previewListing.is_featured && (
                      <Badge className="bg-yellow-500">Mis en avant</Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>

      
    </div>
  );
};

// Main Home Component
const Home = () => {
  const { user, updateProfile } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeView, setActiveView] = useState('home');
  const [selectedListing, setSelectedListing] = useState(null);
  const [cities, setCities] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [editingListing, setEditingListing] = useState(null);
  const [editListing, setEditListing] = useState(null);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [profileFilterType, setProfileFilterType] = useState('all'); // all | sale | rent
  const [profileFilterOpen, setProfileFilterOpen] = useState(false);
  const [newPublishOpen, setNewPublishOpen] = useState(false);
  const [viewListing, setViewListing] = useState(null);
  
  // Fonction pour gérer les URLs d'images (Cloudinary + local)
  const getImageUrl = (imageUrlOrFilename) => {
    // Si c'est déjà une URL complète (Cloudinary ou autre), la retourner directement
    if (imageUrlOrFilename && (imageUrlOrFilename.startsWith('http') || imageUrlOrFilename.startsWith('https'))) {
      return imageUrlOrFilename;
    }
    // Sinon, c'est un ancien fichier local, construire l'URL locale
    return `${BACKEND_URL}/uploads/${imageUrlOrFilename}`;
  };
  
  // Only one card's comments open at a time across lists
  const [openCommentsListingId, setOpenCommentsListingId] = useState(null);
  const [viewIsFavorite, setViewIsFavorite] = useState(false);
  const [viewDescExpanded, setViewDescExpanded] = useState(false);
  const [filters, setFilters] = useState({
    city: '',
    neighborhood: '',
    listing_type: '',
    price_min: '',
    price_max: '',
    surface_min: '',
    surface_max: '',
    rooms_min: '',
    rooms_max: ''
  });

  // Charger les villes au montage et les quartiers lorsque la ville change
  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    fetchNeighborhoods(filters.city);
  }, [filters.city]);

  useEffect(() => {
    fetchListings();
    if (user) {
      fetchFavorites();
      fetchMyListings();
    }
  }, [user]);

  // Charger mes annonces quand on va sur "Mes annonces" ou "Mon compte"
  useEffect(() => {
    if (user && (activeView === 'profile' || activeView === 'account')) {
      fetchMyListings();
    }
  }, [activeView, user]);

  const fetchListings = async (searchParams = {}) => {
    setLoading(true);
    try {
      const params = {
        random_order: true,
        limit: 20,
        ...searchParams
      };
      
      const response = await axios.get(`${API}/listings`, { params });
      setListings(response.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
      toast.error('Erreur lors du chargement des annonces');
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await axios.get(`${API}/cities`);
      setCities(response.data);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchNeighborhoods = async (city) => {
    try {
      if (!city) {
        setNeighborhoods([]);
        return;
      }
      const response = await axios.get(`${API}/neighborhoods`, { params: { city } });
      const data = response.data || [];
      setNeighborhoods(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching neighborhoods:', error);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`);
      setFavorites(response.data);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const fetchMyListings = async () => {
    try {
      const response = await axios.get(`${API}/my-listings`);
      setMyListings(response.data);
    } catch (error) {
      console.error('Error fetching my listings:', error);
    }
  };

  const handleSearch = () => {
    const searchParams = {
      search: searchQuery || undefined,
      ...Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '')
      )
    };
    fetchListings(searchParams);
  };

  const handleApplyFilters = () => {
    handleSearch();
  };

  // Vérifier favori quand on ouvre la vue détaillée
  useEffect(() => {
    const checkFav = async () => {
      try {
        if (viewListing) {
          const resp = await axios.get(`${API}/favorites/${viewListing.id}/check`);
          setViewIsFavorite(!!resp.data.is_favorite);
        }
      } catch (_) {}
    };
    checkFav();
    setViewDescExpanded(false);
  }, [viewListing]);

  const toggleFavoriteView = async () => {
    if (!viewListing) return;
    try {
      if (viewIsFavorite) {
        await axios.delete(`${API}/favorites/${viewListing.id}`);
        setViewIsFavorite(false);
        toast.success('Retiré des favoris');
      } else {
        await axios.post(`${API}/favorites/${viewListing.id}`);
        setViewIsFavorite(true);
        toast.success('Ajouté aux favoris');
      }
    } catch (e) {
      toast.error('Erreur favoris');
    }
  };

  const shareCurrentListing = async () => {
    if (!viewListing) return;
    const url = window.location.href;
    const text = `${viewListing.title} • ${new Intl.NumberFormat('fr-FR').format(viewListing.price)} XAF`;
    try {
      if (navigator.share) {
        await navigator.share({ title: viewListing.title, text, url });
      } else {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        toast.success('Lien copié');
      }
    } catch (_) {}
  };

  const handleMarkerClick = (listing) => {
    setSelectedListing(listing);
    setActiveView('map');
  };

  const handleDeleteListing = async (listingId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette annonce ?')) {
      try {
        await axios.delete(`${API}/listings/${listingId}`);
        toast.success('Annonce supprimée');
        fetchMyListings();
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <div className="aspect-[4/3] bg-gray-200"></div>
              <CardContent className="p-4">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="h-6 bg-gray-200 rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }

    switch (activeView) {
      case 'home':
        return (
          <div key="home-view" className="space-y-6">
            <HeroSection />
            <div className="container mx-auto px-4 py-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                  Dernières annonces ({listings.length})
                </h2>
                <Button
                  onClick={() => fetchListings()}
                  variant="outline"
                  size="sm"
                  className="h-9 sm:h-10"
                >
                  Actualiser
                </Button>
              </div>
              <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 [column-gap:0.75rem]">
                {[...listings]
                  .sort((a, b) => (b.is_featured === true) - (a.is_featured === true))
                  .slice(0, 12)
                  .map((listing) => (
                  <div key={`home-listing-${listing.id}`} className="break-inside-avoid mb-3 sm:mb-4">
                    <ListingCard 
                      listing={listing} 
                      onMarkerClick={handleMarkerClick}
                      onView={(l) => setViewListing(l)}
                      commentsOpen={openCommentsListingId === listing.id}
                      onToggleComments={(open) => setOpenCommentsListingId(open ? listing.id : null)}
                    />
                  </div>
                ))}
              </div>
              {listings.length > 12 && (
                <div className="text-center mt-8">
                  <Button 
                    onClick={() => setActiveView('search')}
                    variant="outline"
                    className="h-10 sm:h-11"
                  >
                    Voir toutes les annonces
                  </Button>
                </div>
              )}
            </div>
          </div>
        );

      case 'search':
        return (
          <div key="search-view" className="flex flex-col lg:flex-row gap-4 lg:gap-6">
            <div className="w-full lg:w-64 lg:flex-shrink-0">
              <FiltersSidebar
                filters={filters}
                setFilters={setFilters}
                onApplyFilters={handleApplyFilters}
                cities={cities}
                neighborhoods={neighborhoods}
              />
            </div>
            <div className="flex-1 min-w-0 p-3 sm:p-4 lg:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Biens ({listings.length})
                </h2>
                <Button
                  onClick={() => fetchListings()}
                  variant="outline"
                  size="sm"
                  className="h-8 sm:h-9 whitespace-nowrap px-3"
                >
                  Actualiser
                </Button>
              </div>
              <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 [column-gap:0.75rem]">
                {[...listings]
                  .sort((a, b) => (b.is_featured === true) - (a.is_featured === true))
                  .map((listing) => (
                  <div key={`search-listing-${listing.id}`} className="break-inside-avoid mb-3 sm:mb-4">
                    <ListingCard 
                      listing={listing} 
                      onMarkerClick={handleMarkerClick}
                      onView={(l) => setViewListing(l)}
                      commentsOpen={openCommentsListingId === listing.id}
                      onToggleComments={(open) => setOpenCommentsListingId(open ? listing.id : null)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'map':
        return (
          <div key="map-view-container" className="h-screen">
            <Map
              key="map-view"
              listings={listings}
              onMarkerClick={(listing) => {
                setSelectedListing(listing);
                setViewListing(listing);
              }}
              height="100%"
            />
          </div>
        );

      case 'favorites':
        return (
          <div key="favorites-view" className="container mx-auto px-4 py-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Mes favoris</h2>
            {favorites.length === 0 ? (
              <div className="text-center py-16">
                <Star className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  Aucun favori pour le moment
                </h3>
                <p className="text-gray-500">
                  Ajoutez des annonces à vos favoris pour les retrouver ici
                </p>
              </div>
            ) : (
              <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 [column-gap:0.75rem]">
                {[...favorites]
                  .sort((a, b) => (b.is_featured === true) - (a.is_featured === true))
                  .map((listing) => (
                  <div key={`favorite-listing-${listing.id}`} className="break-inside-avoid mb-3 sm:mb-4">
                    <ListingCard 
                      listing={listing} 
                      onMarkerClick={handleMarkerClick}
                      onView={(l) => setViewListing(l)}
                      commentsOpen={openCommentsListingId === listing.id}
                      onToggleComments={(open) => setOpenCommentsListingId(open ? listing.id : null)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'admin':
        return <AdminPanel />;

      case 'account':
        return (
          <div key="account-view" className="container mx-auto px-4 py-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Mon compte</h2>
            {/* Résumé des publications */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <Card>
                <CardHeader>
                  <CardTitle>Total annonces</CardTitle>
                </CardHeader>
                <CardContent className="text-3xl font-bold">{myListings.length}</CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Likes cumulés</CardTitle>
                </CardHeader>
                <CardContent className="text-3xl font-bold">{myListings.reduce((s, l) => s + (l.likes_count || 0), 0)}</CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Commentaires cumulés</CardTitle>
                </CardHeader>
                <CardContent className="text-3xl font-bold">{myListings.reduce((s, l) => s + (l.comments_count || 0), 0)}</CardContent>
              </Card>
            </div>

            <AccountForm user={user} updateProfile={updateProfile} />
          </div>
        );

      case 'profile':
        return (
          <div key="profile-view" className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Mes annonces</h2>
              <div className="flex items-center gap-2">
                <Label>Filtrer:</Label>
                <select
                  className="h-9 w-40 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm"
                  value={profileFilterType}
                  onChange={(e) => setProfileFilterType(e.target.value)}
                >
                  <option value="all">Toutes</option>
                  <option value="sale">Vente</option>
                  <option value="rent">Location</option>
                </select>
              </div>
            </div>

            {myListings.length === 0 ? (
              <div className="text-center py-16">
                <HomeIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">Aucune annonce publiée</h3>
                <p className="text-gray-500 mb-4">Commencez par publier votre première annonce</p>
                <Button
                  type="button"
                  onClick={() => {
                    if (paymentService.getCredits() <= 0) {
                      toast.error('Paiement requis : 10 000 FCFA par publication');
                      window.location.assign('/payment?purchase=credit');
                      return;
                    }
                    setNewPublishOpen(true);
                  }}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Publier une annonce
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table key={`table-${profileFilterType}`} className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider p-3 whitespace-nowrap">Titre</th>
                      <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider p-3 whitespace-nowrap">Type</th>
                      <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider p-3 whitespace-nowrap">Prix</th>
                      <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider p-3 whitespace-nowrap">Engagement</th>
                      <th className="text-center text-xs font-semibold text-gray-600 uppercase tracking-wider p-3 whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myListings
                      .filter(l => profileFilterType === 'all' ? true : l.listing_type === profileFilterType)
                      .map((l) => {
                        const likes = l.likes_count || 0;
                        const comments = l.comments_count || 0;
                        const total = Math.max(likes + comments, 1);
                        const likePct = Math.round((likes / total) * 100);
                        const commentPct = 100 - likePct;
                        return (
                          <tr 
                            key={l.id} 
                            className="border-t relative z-0 hover:bg-gray-50 cursor-pointer"
                            onClick={() => setViewListing(l)}
                          >
                            <td className="p-3 whitespace-nowrap">
                              <button
                                type="button"
                                className="font-medium text-left text-gray-900 line-clamp-1 hover:underline max-w-xs sm:max-w-sm md:max-w-md"
                                onClick={() => {
                                  setProfileFilterOpen(false);
                                  setTimeout(() => setViewListing(l), 0);
                                }}
                              >
                                {l.title}
                              </button>
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              <Badge variant="secondary" className="text-xs">{l.listing_type === 'sale' ? 'Vente' : 'Location'}</Badge>
                            </td>
                            <td className="p-3 text-right whitespace-nowrap">
                              <span className="text-sm sm:text-base">{new Intl.NumberFormat('fr-FR').format(l.price)} XAF</span>
                            </td>
                            <td className="p-3 hidden sm:table-cell">
                              <div className="text-xs text-gray-600 mb-1">{likes} likes • {comments} com.</div>
                              <div className="h-2 w-full bg-gray-200 rounded">
                                <div className="h-2 bg-blue-500 rounded-l inline-block" style={{ width: `${likePct}%` }} />
                                <div className="h-2 bg-green-500 rounded-r inline-block" style={{ width: `${commentPct}%` }} />
                              </div>
                            </td>
                            <td className="p-3 text-center whitespace-nowrap">
                              <div className="flex items-center justify-center gap-1 sm:gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="secondary"
                                  className="h-8 px-2 text-xs"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setViewListing(l);
                                  }}
                                >
                                  <Eye className="w-3 h-3 sm:mr-1" /> 
                                  <span className="hidden sm:inline">Voir</span>
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-8 px-2 text-xs"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (typeof setProfileFilterOpen === 'function') setProfileFilterOpen(false);
                                    setTimeout(() => setEditingListing({ ...l }), 0);
                                  }}
                                >
                                  <Edit className="w-3 h-3 sm:mr-1" /> 
                                  <span className="hidden sm:inline">Éditer</span>
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive" 
                                  className="h-8 px-2 text-xs"
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    handleDeleteListing(l.id); 
                                  }}
                                >
                                  <Trash2 className="w-3 h-3 sm:mr-1" /> 
                                  <span className="hidden sm:inline">Suppr</span>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}

            {/* New Listing Dialog (Profile view) */}
            <Dialog key={`new-publish-${newPublishOpen ? 'open' : 'closed'}`} open={newPublishOpen} onOpenChange={setNewPublishOpen}>
              {newPublishOpen && (
                <PublishDialog key={`form-new`} onClose={() => setNewPublishOpen(false)} />
              )}
            </Dialog>

            {/* Edit Listing Dialog (Profile view) */}
            <Dialog key={`edit-${editingListing?.id || 'none'}`} open={!!editingListing} onOpenChange={(open) => { if (!open) setEditingListing(null); }}>
              {editingListing && (
                <PublishDialog key={`form-${editingListing.id}`} onClose={() => setEditingListing(null)} editListing={editingListing} />
              )}
            </Dialog>

          </div>
        );

      default:
        return (
          <div key="home-view" className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Biens immobiliers ({listings.length} résultat{listings.length > 1 ? 's' : ''})
              </h2>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveView('map')}
                  className="flex items-center h-9 sm:h-10 px-2 sm:px-3 text-xs sm:text-sm"
                >
                  <MapIcon className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Vue Carte</span>
                </Button>
                <Button
                  onClick={() => fetchListings()}
                  variant="outline"
                  size="sm"
                  className="h-9 sm:h-10 px-2 sm:px-3 text-xs sm:text-sm"
                >
                  Actualiser
                </Button>
              </div>
            </div>

            {listings.length === 0 ? (
              <div className="text-center py-16">
                <HomeIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  Aucune annonce trouvée
                </h3>
                <p className="text-gray-500 mb-4">
                  Essayez de modifier vos critères de recherche
                </p>
                <Button 
                  onClick={() => {
                    setSearchQuery('');
                    setFilters({
                      city: '',
                      neighborhood: '',
                      listing_type: '',
                      price_min: '',
                      price_max: '',
                      surface_min: '',
                      surface_max: '',
                      rooms_min: '',
                      rooms_max: ''
                    });
                    fetchListings();
                  }}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Voir toutes les annonces
                </Button>
              </div>
            ) : (
              <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 [column-gap:0.75rem]">
                {[...listings]
                  .sort((a, b) => (b.is_featured === true) - (a.is_featured === true))
                  .map((listing) => (
                  <div key={`profile-listing-${listing.id}`} className="break-inside-avoid mb-3 sm:mb-4">
                    <ListingCard 
                      listing={listing} 
                      onMarkerClick={handleMarkerClick}
                      onView={(l) => setViewListing(l)}
                      commentsOpen={openCommentsListingId === listing.id}
                      onToggleComments={(open) => setOpenCommentsListingId(open ? listing.id : null)}
                    />
                  </div>
                ))}
              </div>
            )}

            {listings.length > 0 && (
              <div className="text-center mt-8">
                <Button 
                  onClick={() => fetchListings()}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Voir plus d'annonces
                </Button>
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <Header 
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        activeView={activeView}
        setActiveView={setActiveView}
      />

      <main className="min-w-0">
        {renderContent()}
      </main>

      {/* Global View Listing Dialog for buyers */}
      <Dialog key={`global-view-${viewListing?.id || 'none'}`} open={!!viewListing} onOpenChange={(open) => { if (!open) setViewListing(null); }}>
        {viewListing && (
          <DialogContent className="w-screen h-screen sm:w-[95vw] sm:h-auto sm:max-w-5xl sm:max-h-[95vh] overflow-y-auto overflow-x-hidden p-0 m-0 sm:m-4 rounded-none sm:rounded-lg flex flex-col">
            <DialogHeader className="sr-only">
              <DialogTitle>{viewListing.title}</DialogTitle>
              <DialogDescription>
                {viewListing.city}{viewListing.neighborhood ? `, ${viewListing.neighborhood}` : ''}
              </DialogDescription>
            </DialogHeader>
            {/* Header avec carrousel d'images */}
            <div className="relative h-[38vh] min-h-72 max-h-[520px] sm:h-64 md:h-80 overflow-hidden flex-shrink-0">
              {viewListing.images && viewListing.images.length > 0 ? (
                <div className="relative w-full h-full">
                  <img 
                    src={getImageUrl(viewListing.images[currentImageIndex])}
                    alt={viewListing.title}
                    className="w-full h-full object-cover transition-all duration-300"
                  />
                  
                  {/* Boutons de navigation */}
                  {viewListing.images.length > 1 && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex(prev => prev === 0 ? viewListing.images.length - 1 : prev - 1);
                        }}
                        className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 sm:p-3 rounded-full transition-colors z-20"
                      >
                        <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex(prev => prev === viewListing.images.length - 1 ? 0 : prev + 1);
                        }}
                        className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 sm:p-3 rounded-full transition-colors z-20"
                      >
                        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                      
                      {/* Indicateurs */}
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 sm:gap-3">
                        {viewListing.images.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentImageIndex(idx);
                            }}
                            className={`w-3 h-3 sm:w-2 sm:h-2 rounded-full transition-colors z-20 ${
                              idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                  <HomeIcon className="w-16 sm:w-24 h-16 sm:h-24 text-white opacity-50" />
                </div>
              )}
              
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              
              {/* Actions en haut */}
              <div className="absolute top-2 sm:top-4 left-2 sm:left-4 right-2 sm:right-4 flex items-center justify-between z-10">
                <Button variant="secondary" size="sm" onClick={() => { setViewListing(null); setCurrentImageIndex(0); }} className="bg-white/90 hover:bg-white text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3">
                  <X className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> <span className="hidden sm:inline">Retour</span>
                </Button>
                <div className="flex items-center gap-1 sm:gap-2">
                  <Button variant={viewIsFavorite ? 'default' : 'secondary'} size="sm" onClick={toggleFavoriteView} className={`text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3 ${viewIsFavorite ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-white/90 hover:bg-white'}`}>
                    <Heart className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 ${viewIsFavorite ? 'fill-current' : ''}`} /> 
                    <span className="hidden sm:inline">{viewIsFavorite ? 'Favori' : 'Ajouter'}</span>
                  </Button>
                  <Button variant="secondary" size="sm" onClick={shareCurrentListing} className="bg-white/90 hover:bg-white text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3">
                    <Share2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> <span className="hidden sm:inline">Partager</span>
                  </Button>
                </div>
              </div>

              {/* Badge type */}
              <div className="absolute top-2 sm:top-4 left-1/2 transform -translate-x-1/2 z-10">
                <Badge className={`px-2 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm font-medium ${viewListing.listing_type === 'sale' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white`}>
                  {viewListing.listing_type === 'rent' ? 'À LOUER' : 'À VENDRE'}
                </Badge>
              </div>
              
              {/* Infos principales en bas */}
              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6 text-white z-10">
                <h1 className="text-lg sm:text-2xl font-bold mb-2 line-clamp-2">{viewListing.title}</h1>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center gap-2 text-white/90">
                    <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="text-sm sm:text-base">{viewListing.city}{viewListing.neighborhood ? `, ${viewListing.neighborhood}` : ''}</span>
                  </div>
                  <div className="text-xl sm:text-3xl font-bold">
                    {new Intl.NumberFormat('fr-FR').format(viewListing.price)} XAF
                  </div>
                </div>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4 md:space-y-6">
                
                {/* Mini galerie d'images */}
                {Array.isArray(viewListing.images) && viewListing.images.length > 1 && (
                  <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2">
                    {viewListing.images.map((img, idx) => (
                      <img
                        key={`${viewListing.id}-thumb-${idx}`}
                        src={getImageUrl(img)}
                        alt={`${viewListing.title} ${idx + 1}`}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`h-16 sm:h-16 md:h-20 w-24 sm:w-24 md:w-32 object-cover rounded-lg border-2 transition-colors cursor-pointer flex-shrink-0 ${
                          idx === currentImageIndex ? 'border-orange-400' : 'border-gray-200 hover:border-orange-400'
                        }`}
                      />
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                  <div className="lg:col-span-2 space-y-6">

                    {/* Description */}
                    {viewListing.description && (
                      <Card className="border-0 shadow-sm">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg font-semibold text-gray-900">Description</CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="text-gray-700 leading-relaxed">
                            {viewDescExpanded ? viewListing.description : (viewListing.description.length > 300 ? `${viewListing.description.slice(0, 300)}...` : viewListing.description)}
                          </div>
                          {viewListing.description.length > 300 && (
                            <Button variant="ghost" size="sm" className="mt-3 px-0 h-auto text-orange-600 hover:text-orange-700" onClick={() => setViewDescExpanded(!viewDescExpanded)}>
                              {viewDescExpanded ? 'Lire moins' : 'Lire plus'}
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {/* Caractéristiques */}
                    {(viewListing.surface || viewListing.rooms || viewListing.bathrooms || viewListing.floor || viewListing.year_built) && (
                      <Card className="border-0 shadow-sm">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg font-semibold text-gray-900">Caractéristiques</CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                            {viewListing.surface && (
                              <div className="text-center p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                                <div className="text-xl sm:text-2xl font-bold text-blue-700">{viewListing.surface}</div>
                                <div className="text-xs sm:text-sm text-blue-600 font-medium">m²</div>
                              </div>
                            )}
                            {viewListing.rooms && (
                              <div className="text-center p-3 sm:p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
                                <div className="text-xl sm:text-2xl font-bold text-green-700">{viewListing.rooms}</div>
                                <div className="text-xs sm:text-sm text-green-600 font-medium">Pièces</div>
                              </div>
                            )}
                            {viewListing.bathrooms && (
                              <div className="text-center p-3 sm:p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200">
                                <div className="text-xl sm:text-2xl font-bold text-purple-700">{viewListing.bathrooms}</div>
                                <div className="text-xs sm:text-sm text-purple-600 font-medium">SDB</div>
                              </div>
                            )}
                            {viewListing.floor && (
                              <div className="text-center p-3 sm:p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200">
                                <div className="text-xl sm:text-2xl font-bold text-orange-700">{viewListing.floor}{viewListing.total_floors ? `/${viewListing.total_floors}` : ''}</div>
                                <div className="text-xs sm:text-sm text-orange-600 font-medium">Étage</div>
                              </div>
                            )}
                            {viewListing.year_built && (
                              <div className="text-center p-3 sm:p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                                <div className="text-xl sm:text-2xl font-bold text-gray-700">{viewListing.year_built}</div>
                                <div className="text-xs sm:text-sm text-gray-600 font-medium">Année</div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Équipements et Services */}
                    {(viewListing.kitchen_equipped || viewListing.balcony || viewListing.ac || viewListing.parking || viewListing.garage || viewListing.heating || (viewListing.availability && viewListing.availability !== 'immediate')) && (
                      <Card className="border-0 shadow-sm">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg font-semibold text-gray-900">Équipements & Services</CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                            {viewListing.kitchen_equipped && (
                              <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                                  <span className="text-white text-xs sm:text-sm">✓</span>
                                </div>
                                <span className="text-sm sm:text-base font-medium text-emerald-800">Cuisine équipée</span>
                              </div>
                            )}
                            {viewListing.balcony && (
                              <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                                  <span className="text-white text-xs sm:text-sm">✓</span>
                                </div>
                                <span className="text-sm sm:text-base font-medium text-emerald-800">Balcon/Terrasse</span>
                              </div>
                            )}
                            {viewListing.ac && (
                              <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-sky-50 rounded-lg border border-sky-200">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-sky-500 rounded-full flex items-center justify-center">
                                  <span className="text-white text-xs">❄️</span>
                                </div>
                                <span className="text-sm sm:text-base font-medium text-sky-800">Climatisation</span>
                              </div>
                            )}
                            {viewListing.parking && (
                              <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-indigo-500 rounded-full flex items-center justify-center">
                                  <span className="text-white text-xs">🚗</span>
                                </div>
                                <span className="text-sm sm:text-base font-medium text-indigo-800">Parking</span>
                              </div>
                            )}
                            {viewListing.garage && (
                              <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-indigo-500 rounded-full flex items-center justify-center">
                                  <span className="text-white text-xs">🏠</span>
                                </div>
                                <span className="text-sm sm:text-base font-medium text-indigo-800">Garage</span>
                              </div>
                            )}
                            {viewListing.heating && (
                              <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-red-50 rounded-lg border border-red-200">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-500 rounded-full flex items-center justify-center">
                                  <span className="text-white text-xs">🔥</span>
                                </div>
                                <span className="text-sm sm:text-base font-medium text-red-800">
                                  {viewListing.heating === 'electric' ? 'Chauffage électrique' : 
                                   viewListing.heating === 'gas' ? 'Chauffage gaz' : 
                                   viewListing.heating === 'solar' ? 'Chauffage solaire' : 'Chauffage'}
                                </span>
                              </div>
                            )}
                            {viewListing.availability && viewListing.availability !== 'immediate' && (
                              <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-amber-50 rounded-lg border border-amber-200">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-amber-500 rounded-full flex items-center justify-center">
                                  <span className="text-white text-xs">📅</span>
                                </div>
                                <span className="text-sm sm:text-base font-medium text-amber-800">
                                  {viewListing.availability === '1month' ? 'Disponible dans 1 mois' : 
                                   viewListing.availability === '3months' ? 'Disponible dans 3 mois' : 
                                   viewListing.availability === '6months' ? 'Disponible dans 6 mois' : 'Disponibilité à convenir'}
                                </span>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Carte intégrée */}
                    {viewListing.lat && viewListing.lon && (
                      <Card className="border-0 shadow-sm">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg font-semibold text-gray-900">Localisation</CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="h-64 bg-gray-100 rounded-xl mb-4 overflow-hidden">
                            <Map
                              center={[viewListing.lat, viewListing.lon]}
                              zoom={15}
                              markers={[{
                                id: viewListing.id,
                                lat: viewListing.lat,
                                lng: viewListing.lon,
                                title: viewListing.title,
                                price: viewListing.price,
                                listing_type: viewListing.listing_type
                              }]}
                              onMarkerClick={() => {}}
                              className="h-full w-full"
                            />
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <Button asChild size="sm" variant="outline" className="h-auto py-3 flex-col gap-1">
                              <a href={`https://www.google.com/maps/dir/?api=1&destination=${viewListing.lat},${viewListing.lon}`} target="_blank" rel="noreferrer">
                                <MapIcon className="w-5 h-5" />
                                <span className="text-xs">Itinéraire</span>
                              </a>
                            </Button>
                            <Button asChild size="sm" variant="outline" className="h-auto py-3 flex-col gap-1">
                              <a href={`https://www.google.com/maps/search/école+près+de+${viewListing.lat},${viewListing.lon}`} target="_blank" rel="noreferrer">
                                <span className="text-lg">🏫</span>
                                <span className="text-xs">Écoles</span>
                              </a>
                            </Button>
                            <Button asChild size="sm" variant="outline" className="h-auto py-3 flex-col gap-1">
                              <a href={`https://www.google.com/maps/search/commerce+près+de+${viewListing.lat},${viewListing.lon}`} target="_blank" rel="noreferrer">
                                <span className="text-lg">🛒</span>
                                <span className="text-xs">Commerces</span>
                              </a>
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>

                  {/* Sidebar contact */}
                  <div className="space-y-6">
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-orange-50 to-orange-100">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg font-semibold text-orange-900">Contacter le vendeur</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0 space-y-4">
                        {(() => {
                          const sellerName = viewListing.user?.name || viewListing.owner_name || 'Vendeur';
                          const sellerPhone = viewListing.user?.phone || viewListing.phone || '';
                          const sellerEmail = viewListing.user?.email || viewListing.email || '';
                          
                          return (
                            <>
                              <div className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm">
                                <Avatar className="h-12 w-12">
                                  <AvatarFallback className="bg-orange-500 text-white font-bold text-lg">
                                    {sellerName[0]?.toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <div className="font-semibold text-gray-900">{sellerName}</div>
                                  <div className="text-sm text-orange-600">Propriétaire</div>
                                </div>
                              </div>
                              
                              <div className="space-y-3">
                                {sellerPhone && (
                                  <Button asChild className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-medium">
                                    <a href={`tel:${sellerPhone}`} className="flex items-center justify-center gap-3">
                                      <Phone className="w-5 h-5" /> 
                                      <span>Appeler maintenant</span>
                                    </a>
                                  </Button>
                                )}
                                {sellerPhone && (
                                  <Button asChild variant="outline" className="w-full h-12 border-green-200 hover:bg-green-50 font-medium">
                                    <a href={`https://wa.me/${sellerPhone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-3">
                                      <MessageCircle className="w-5 h-5 text-green-600" /> 
                                      <span>WhatsApp</span>
                                    </a>
                                  </Button>
                                )}
                                {sellerEmail && (
                                  <Button asChild variant="outline" className="w-full h-12 border-blue-200 hover:bg-blue-50 font-medium">
                                    <a href={`mailto:${sellerEmail}?subject=Intéressé par ${viewListing.title}`} className="flex items-center justify-center gap-3">
                                      <Mail className="w-5 h-5 text-blue-600" /> 
                                      <span>Envoyer un email</span>
                                    </a>
                                  </Button>
                                )}
                                {!sellerPhone && !sellerEmail && (
                                  <div className="text-center text-sm text-gray-500 py-4">
                                    Aucune information de contact disponible
                                  </div>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/payment" element={<PaymentPage />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;